import { createContext, useContext } from 'react';
import { DndContext, KeyboardSensor, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { restrictToParent } from './sortableModifiers.js';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical } from 'lucide-react';
import cn from './cn.js';

const HandleContext = createContext(null);

/**
 * SortableList: vertical drag & drop reordering (mouse, touch and keyboard:
 * focus the handle, Space to pick up, ↑/↓ to move, Space to drop, Esc to cancel).
 *
 * items: array of objects with a stable `id`
 * onMove(fromIndex, toIndex)
 * renderItem(item, index) must render a <SortableItem id={item.id}>.
 */
export default function SortableList({ items, onMove, children, as: Comp = 'ol', className }) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const position = (id) => items.findIndex((item) => item.id === id) + 1;
  const announcements = {
    onDragStart: ({ active }) => `Picked up item ${position(active.id)} of ${items.length}.`,
    onDragOver: ({ active, over }) => (over ? `Item ${position(active.id)} is over position ${position(over.id)} of ${items.length}.` : undefined),
    onDragEnd: ({ active, over }) => (over ? `Item moved to position ${position(over.id)} of ${items.length}.` : `Item ${position(active.id)} dropped.`),
    onDragCancel: ({ active }) => `Reordering cancelled. Item ${position(active.id)} returned to its place.`,
  };

  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const from = items.findIndex((item) => item.id === active.id);
    const to = items.findIndex((item) => item.id === over.id);
    if (from >= 0 && to >= 0) onMove(from, to);
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
      modifiers={[restrictToParent]}
      accessibility={{
        announcements,
        screenReaderInstructions: { draggable: 'Press Space or Enter to pick up. Use the arrow keys to move, Space or Enter to drop, Escape to cancel.' },
      }}
    >
      <SortableContext items={items.map((item) => item.id)} strategy={verticalListSortingStrategy}>
        <Comp className={className}>{children}</Comp>
      </SortableContext>
    </DndContext>
  );
}

/** One draggable row. Put a <DragHandle /> anywhere inside it. */
export function SortableItem({ id, as: Comp = 'li', className, children, disabled = false }) {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({ id, disabled });
  const style = { transform: CSS.Translate.toString(transform), transition };

  return (
    <HandleContext.Provider value={{ attributes, listeners, setActivatorNodeRef, disabled }}>
      <Comp ref={setNodeRef} style={style} className={cn(className, 'relative', isDragging && 'z-10 shadow-pop ring-indigo-300')}>
        {children}
      </Comp>
    </HandleContext.Provider>
  );
}

/** Grip used to drag the surrounding SortableItem. */
export function DragHandle({ label = 'Drag to reorder', className, children }) {
  const ctx = useContext(HandleContext);
  if (!ctx) return null;
  const { attributes, listeners, setActivatorNodeRef, disabled } = ctx;
  return (
    <button
      type="button"
      ref={setActivatorNodeRef}
      {...attributes}
      {...listeners}
      aria-label={label}
      title={label}
      disabled={disabled}
      className={cn(
        'inline-flex shrink-0 touch-none select-none items-center justify-center gap-1 rounded-md text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500',
        disabled ? 'cursor-not-allowed opacity-40' : 'cursor-grab active:cursor-grabbing',
        className,
      )}
    >
      <GripVertical className="h-4 w-4" aria-hidden="true" />
      {children}
    </button>
  );
}
